import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Mail, AlertCircle, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../context/AppContext";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";

export function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("login"); // login | forgot
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  React.useEffect(() => {
    if (user) navigate("/admin/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setForgotSent(true);
      } else {
        await login(email, password);
        navigate("/admin/dashboard", { replace: true });
      }
    } catch (err) {
      setError(fmtErr(err, mode === "forgot" ? "Could not process that request." : "Login failed. Check your email and password."));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-800 focus:border-amber-500 transition-colors";

  return (
    <main className="min-h-screen bg-[#061325] flex items-center justify-center px-4 py-16" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="font-heading text-4xl text-amber-100 font-semibold">LD Travel</p>
          <p className="text-amber-400/80 text-xs tracking-[0.3em] uppercase mt-2">Owner Dashboard</p>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          {mode === "login" ? (
            <>
              <h1 className="font-heading text-2xl font-semibold text-[#0B1D3A] mb-6">Sign in to manage your website</h1>
              <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
                <div>
                  <label htmlFor="al-email" className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
                    <input id="al-email" type="email" required autoComplete="email" data-testid="admin-login-email" className={`${inputCls} !pl-10`} value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label htmlFor="al-pass" className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Password</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
                    <input id="al-pass" type="password" required autoComplete="current-password" data-testid="admin-login-password" className={`${inputCls} !pl-10`} value={password} onChange={(e) => setPassword(e.target.value)} />
                  </div>
                </div>
                {error && <p role="alert" data-testid="admin-login-error" className="text-red-600 text-sm flex items-center gap-1.5"><AlertCircle className="w-4 h-4" />{error}</p>}
                <button type="submit" disabled={busy} data-testid="admin-login-submit" className="btn-brand w-full justify-center disabled:opacity-60">
                  {busy ? "Signing in…" : "Sign In"}
                </button>
              </form>
              <button onClick={() => { setMode("forgot"); setError(""); }} data-testid="admin-forgot-link" className="text-sm text-amber-700 hover:text-amber-800 font-medium mt-4">
                Forgot your password?
              </button>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-semibold text-[#0B1D3A] mb-3">Reset your password</h1>
              {forgotSent ? (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-sm text-emerald-800 flex items-start gap-2" data-testid="admin-forgot-success" role="status">
                  <CheckCircle2 className="w-5 h-5 shrink-0" />
                  If that email is registered, a reset link is on its way. Check your inbox (and spam folder).
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="admin-forgot-form">
                  <p className="text-sm text-slate-500">Enter your admin email and we'll send you a secure reset link.</p>
                  <div>
                    <label htmlFor="af-email" className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Email</label>
                    <input id="af-email" type="email" required data-testid="admin-forgot-email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  {error && <p role="alert" className="text-red-600 text-sm">{error}</p>}
                  <button type="submit" disabled={busy} data-testid="admin-forgot-submit" className="btn-brand w-full justify-center disabled:opacity-60">
                    {busy ? "Sending…" : "Send Reset Link"}
                  </button>
                </form>
              )}
              <button onClick={() => { setMode("login"); setError(""); setForgotSent(false); }} data-testid="admin-back-to-login" className="text-sm text-amber-700 hover:text-amber-800 font-medium mt-4">
                Back to sign in
              </button>
            </>
          )}
        </div>
        <p className="text-center mt-6">
          <Link to="/" className="text-slate-400 text-sm hover:text-amber-200 transition-colors" data-testid="admin-login-back-home">← Back to website</Link>
        </p>
      </div>
    </main>
  );
}

export function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(fmtErr(err, "Reset link is invalid or has expired. Request a new one."));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-800 focus:border-amber-500 transition-colors";

  return (
    <main className="min-h-screen bg-[#061325] flex items-center justify-center px-4 py-16" data-testid="admin-reset-page">
      <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl">
        <h1 className="font-heading text-2xl font-semibold text-[#0B1D3A] mb-6">Choose a new password</h1>
        {!token ? (
          <p className="text-red-600 text-sm" data-testid="admin-reset-missing-token">This reset link is missing its token. Please request a new link from the sign-in page.</p>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="admin-reset-form">
            <div>
              <label htmlFor="rp-pass" className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">New password</label>
              <input id="rp-pass" type="password" required minLength={8} autoComplete="new-password" data-testid="admin-reset-password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div>
              <label htmlFor="rp-confirm" className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Confirm new password</label>
              <input id="rp-confirm" type="password" required autoComplete="new-password" data-testid="admin-reset-confirm" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
            {error && <p role="alert" data-testid="admin-reset-error" className="text-red-600 text-sm">{error}</p>}
            <button type="submit" disabled={busy} data-testid="admin-reset-submit" className="btn-brand w-full justify-center disabled:opacity-60">
              {busy ? "Updating…" : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
